import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { readPreload } from "../lib/preload";
import { usePageMeta, SEO_CONSTANTS } from "../lib/usePageMeta";
import { FileText, MagnifyingGlass, DownloadSimple, ArrowSquareOut } from "@phosphor-icons/react";

const API_ORIGIN =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" ? window.location.origin : "");

function absolute(pathOrUrl) {
  if (!pathOrUrl) return "#";
  if (/^https?:/i.test(pathOrUrl)) return pathOrUrl;
  return `${API_ORIGIN}${pathOrUrl}`;
}

export default function CoaLibrary() {
  // Prerendered snapshot embeds the list; the fetch below still refreshes it.
  const [rows, setRows] = useState(() => readPreload("/coa-library/rich?limit=500") || []);
  const [loading, setLoading] = useState(() => !readPreload("/coa-library/rich?limit=500"));
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    let mounted = true;
    api
      .get("/coa-library/rich", { params: { limit: 500 } })
      .then((r) => {
        if (!mounted) return;
        setRows(r.data || []);
        setLoading(false);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e?.message || "Failed to load COA library");
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const hay = [
        r.product_display,
        r.product_sku,
        r.lot_number,
        r.laboratory,
      ]
        .map((x) => (x || "").toLowerCase())
        .join(" ");
      return hay.includes(needle);
    });
  }, [rows, q]);

  const currentCount = filtered.filter((r) => r.is_current).length;
  const archivedCount = filtered.length - currentCount;

  usePageMeta({
    title: "COA Library — Batch Certificates",
    description:
      "Browse every published Certificate of Analysis. Search by compound, strength, or lot number to view or download the batch document.",
    canonicalPath: "/coa-library",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": `${SEO_CONSTANTS.PROD_HOST}/coa-library#page`,
        "name": "COA Library",
        "url": `${SEO_CONSTANTS.PROD_HOST}/coa-library`,
        "description":
          "Public library of Certificates of Analysis for every reviewed batch.",
        "publisher": {
          "@type": "Organization",
          "name": "Medical Peptide Store",
        },
      },
    ],
  });

  return (
    <div className="bg-brand-surface min-h-[60vh]" data-testid="coa-library-page">
      <div className="mps-container py-16 md:py-20">
        {/* Header */}
        <div className="max-w-3xl">
          <div className="eyebrow mb-4">COA Library</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-brand-navy leading-tight">
            Batch documents, ready to view.
          </h1>
          <p className="mt-5 text-lg text-brand-muted leading-relaxed">
            Every reviewed batch has a Certificate of Analysis (COA) listed
            below by compound, strength, and lot number. Click a lot to view
            the PDF in your browser or download it locally.
          </p>
        </div>

        {/* Search + counts */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-[1fr,auto] gap-4 items-center">
          <label className="relative block max-w-xl">
            <span className="sr-only">Search COAs</span>
            <MagnifyingGlass
              size={18}
              weight="regular"
              className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none"
              aria-hidden
            />
            <input
              type="search"
              inputMode="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by compound, strength, or lot number…"
              className="w-full h-12 pl-11 pr-4 rounded-[12px] bg-white border border-brand-border text-brand-text placeholder-brand-muted focus:outline-none focus:ring-2 focus:ring-brand-accent"
              data-testid="coa-library-search"
            />
          </label>
          <div
            className="text-sm text-brand-muted"
            data-testid="coa-library-counts"
          >
            <span className="font-semibold text-brand-navy">
              {filtered.length}
            </span>{" "}
            showing · {currentCount} current · {archivedCount} previous
          </div>
        </div>

        {/* States */}
        {loading && (
          <div
            className="mt-10 mps-card p-8 text-brand-muted"
            data-testid="coa-library-loading"
          >
            Loading batch documents…
          </div>
        )}
        {error && !loading && (
          <div
            className="mt-10 mps-card p-8 border-red-200 text-red-700 bg-red-50"
            data-testid="coa-library-error"
          >
            Couldn’t load the COA library — {error}. Please try again or
            email{" "}
            <a
              href="mailto:info@medicalpeptidestore.com"
              className="underline"
            >
              info@medicalpeptidestore.com
            </a>
            .
          </div>
        )}

        {/* Table */}
        {!loading && !error && (
          <div
            className="mt-10 mps-card overflow-hidden"
            data-testid="coa-library-table-card"
          >
            <div className="overflow-x-auto">
              <table
                className="w-full text-left text-sm"
                data-testid="coa-library-table"
              >
                <thead className="bg-brand-surface border-b border-brand-border text-[11px] uppercase tracking-[0.14em] text-brand-muted">
                  <tr>
                    <th className="px-5 py-3 font-semibold">Compound</th>
                    <th className="px-5 py-3 font-semibold">Strength</th>
                    <th className="px-5 py-3 font-semibold">Lot number</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 font-semibold text-right">
                      Document
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-border">
                  {filtered.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-5 py-10 text-center text-brand-muted"
                        data-testid="coa-library-empty"
                      >
                        No batch documents match your search.
                      </td>
                    </tr>
                  )}
                  {filtered.map((r) => {
                    const strength =
                      r.product_display && /\d/.test(r.product_display)
                        ? (r.product_display.match(/(\d[\d.,/]*\s*mg[a-z\/0-9\s]*)/i) ||
                            [])[0] || "—"
                        : "—";
                    return (
                      <tr
                        key={r.id}
                        className="hover:bg-brand-surface transition-colors"
                        data-testid={`coa-library-row-${r.id}`}
                      >
                        <td className="px-5 py-4 align-top">
                          <div className="flex items-start gap-3">
                            <FileText
                              size={16}
                              weight="regular"
                              className="text-brand-accent shrink-0 mt-[2px]"
                              aria-hidden
                            />
                            <div>
                              <div className="font-semibold text-brand-navy leading-snug">
                                {r.product_display || r.product_slug || "—"}
                              </div>
                              {r.product_sku && (
                                <div className="text-[12px] text-brand-muted mt-0.5">
                                  SKU {r.product_sku}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 align-top text-brand-text">
                          {strength}
                        </td>
                        <td className="px-5 py-4 align-top">
                          <span className="font-mono text-[12px] px-2 py-1 rounded bg-brand-surface border border-brand-border text-brand-navy">
                            {r.lot_number || "—"}
                          </span>
                        </td>
                        <td className="px-5 py-4 align-top">
                          {r.is_current ? (
                            <span
                              className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-700"
                              data-testid={`coa-library-row-${r.id}-current`}
                            >
                              <span className="h-2 w-2 rounded-full bg-emerald-500" />
                              Current
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-muted">
                              <span className="h-2 w-2 rounded-full bg-brand-muted/60" />
                              Previous
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-4 align-top text-right whitespace-nowrap">
                          <a
                            href={absolute(r.view_url)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`coa-library-view-${r.id}`}
                            className="inline-flex items-center gap-1 text-sm font-semibold text-brand-navy hover:text-brand-accent transition-colors"
                          >
                            View <ArrowSquareOut size={14} weight="bold" />
                          </a>
                          <span className="text-brand-border mx-2">|</span>
                          <a
                            href={absolute(r.download_url)}
                            data-testid={`coa-library-download-${r.id}`}
                            className="inline-flex items-center gap-1 text-sm font-semibold text-brand-accent hover:text-brand-navy transition-colors"
                          >
                            Download <DownloadSimple size={14} weight="bold" />
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Footnote */}
        <p className="mt-8 text-[13px] text-brand-muted max-w-2xl leading-relaxed">
          These documents are published for reference. If you can’t find the
          lot number from your packing list, email{" "}
          <a
            href="mailto:info@medicalpeptidestore.com"
            className="text-brand-accent hover:underline"
          >
            info@medicalpeptidestore.com
          </a>{" "}
          and we’ll send it. All items are research peptides sold strictly for
          laboratory research. Not for human or animal use.
        </p>
      </div>
    </div>
  );
}
